// ═══════════════════════════════════════════════════════════════════════════
// FORME D'UN NUMÉRO D'ENTREPRISE (BCE) — fonction PURE, zéro dépendance.
//
// Pourquoi ce fichier existe (06/09/2026) : `normaliserBce` vivait dans
// `bce-verify.js`, qui importe `fetch-source` → `pdf-lecture` → `unpdf`. Une
// route qui voulait juste comparer deux numéros tirait donc tout le lecteur PDF
// dans sa lambda — 3,7 Mo d'actifs pdfjs (polices, cmaps, wasm) pour huit lignes
// d'arithmétique. Le garde-fou `unpdf-hors-bundle-serveur` l'a refusé, à raison.
//
// Ce module ne doit JAMAIS rien importer : c'est ce qui permet de l'utiliser
// depuis une route, un script Node brut ou un test, sans traîner de bagage.
// `bce-verify.js` le réexporte, donc les appelants existants ne changent pas.
// ═══════════════════════════════════════════════════════════════════════════

const digits = (v) => String(v ?? '').replace(/\D/g, '');

/**
 * N° d'entreprise BCE → 10 chiffres, ou null si inexploitable.
 *
 * ⚠️ STRICT à dessein : une valeur de 1 à 8 chiffres rend `null` plutôt que
 * d'être complétée par des zéros. Comparer des dossiers sur une BCE complétée
 * ferait matcher « 123 » avec « 0000000123 » — un mauvais rattachement coûte
 * plus cher qu'un rattachement refusé.
 * (`onss-repertoire.js` porte une seconde implémentation, elle PERMISSIVE :
 *  `padStart(10, '0')`. Les deux cohabitent depuis longtemps ; à trancher, mais
 *  pas au détour d'un correctif de rattachement.)
 *
 * @param {*} bce valeur brute — « 1003.859.136 », « BE 1028.230.781 », 1037177052…
 * @returns {string|null} 10 chiffres, ou null
 */
export function normaliserBce(bce) {
  const d = digits(bce);
  if (d.length === 10) return d;
  if (d.length === 9) return '0' + d; // forme courte historique (sans le 0 initial)
  return null;
}

/**
 * Un numéro de TVA belge est valide s'il se ramène à 10 chiffres (via
 * `normaliserBce`, donc BE + 9 ou 10 chiffres, espaces/points tolérés),
 * commence par 0 ou 1, et passe la clé de contrôle MOD 97 (ISO 7064) :
 * les 2 derniers chiffres doivent valoir 97 − (les 8 premiers chiffres
 * modulo 97) — ou 97 quand ce reste vaut 0, faute de quoi la clé serait
 * « 00 ».
 *
 * Algorithme public (identique au numéro d'entreprise BCE, dont le n° de
 * TVA belge est la reprise directe) — vérifié le 10/09/2026 via VatDB
 * (https://vatdb.com/guides/validate-be-vat-number/) et LookupTax
 * (https://lookuptax.com/docs/how-to-verify/vat-verification-belgium),
 * concordants. Ce n'est PAS une constante légale (taux/seuil/plafond) au
 * sens de R1/lois-belges.js : c'est une formule de contrôle mathématique
 * publique, sans date d'entrée en vigueur.
 *
 * @param {*} tva valeur brute — « BE0123456749 », « 0123.456.749 »…
 * @returns {boolean}
 */
export function estNumeroTvaBelgeValide(tva) {
  const d = normaliserBce(tva);
  if (!d) return false;
  if (d[0] !== '0' && d[0] !== '1') return false;
  const base = parseInt(d.slice(0, 8), 10);
  const cle = parseInt(d.slice(8, 10), 10);
  const reste = base % 97;
  const cleAttendue = reste === 0 ? 97 : 97 - reste;
  return cle === cleAttendue;
}
